/**
 * Распределение пришедших денег.
 *
 * `allocateIncome` распределяет **только по целям** — обязательства в него не
 * входят сознательно, они уже вычтены в «Можно потратить». Поэтому назвать
 * его планом поступившего дохода было ошибкой: между приходом и целями стоят
 * платежи ближайших дней и жизнь до следующего надёжного прихода.
 *
 * Здесь один владелец обязательных платежей — этот расчёт. Водопад целей
 * вызывается уже на защищённом остатке и про минимумы по долгам не знает,
 * иначе минимум по карте выделялся бы дважды.
 */
import { ZERO, add, clampPos, share, sub, type Money } from './money.js';
import { addDays, diffDays, ymd, type PlainDate } from './date.js';
import { isKnown, type Amount } from './amount.js';
import { allocateIncome, type Allocation } from './goals.js';
import { paymentInstances } from './instances.js';
import type { TradeoffChoice } from './priority.js';
import type { Account, CreditTerms, Goal, Loan, Obligation, Reserve } from './types.js';

/**
 * Период защиты повседневных расходов.
 *
 * `until`/`days` равны `null`, когда периода нет: дата следующего прихода
 * неизвестна, и человек не назвал срок сам. Раньше на этом месте стояли
 * тридцать дней — и распределение защищало выдуманный месяц, раскладывая
 * остаток по целям так, будто срок известен. Приложение честно писало, что
 * дата неизвестна, и всё равно действовало по ней; менять надо было
 * поведение, а не подпись.
 */
export interface ProtectWindow {
  until: PlainDate | null;
  days: number | null;
  /** Откуда взялся период — это показывается человеку. */
  source: 'horizon' | 'user' | 'unknown';
}

export interface DistributionInput {
  /** Сколько реально пришло. */
  amount: Money;
  today: PlainDate;
  /** Дата ближайшего надёжного прихода. `null` — неизвестна. */
  nextIncomeDate: PlainDate | null;
  obligations: readonly Obligation[];
  reserves?: readonly Reserve[];
  /** Обычные траты на повседневные нужды в месяц. «Не знаю» здесь меняет весь ответ. */
  needBaseline: Amount;
  goals: Goal[];
  accounts: Account[];
  creditTerms: CreditTerms[];
  loans?: Loan[];
  monthlyNeed: Money;
  /** Решение человека, если он выбирал между резервом и гашением. */
  choice?: TradeoffChoice;
  /**
   * Явно выбранный период защиты. Без него берётся срок до ближайшего
   * прихода; если и его нет, периода нет вовсе — см. `ProtectWindow`.
   */
  protectDays?: number;
}

export interface DistributionResult {
  /** Необеспеченные платежи до конца периода защиты. */
  obligations: {
    instanceId: string; label: string; dueDate: PlainDate; amount: Money; reason: string;
  }[];
  /** Деньги повседневные нужды. `null` — расходы неизвестны, и сумму придумывать нельзя. */
  living: Money | null;
  /** Что человек выбрал сам: резерв или гашение. Идёт до водопада. */
  choice: Allocation | null;
  /** Остаток по целям — через существующий водопад. */
  goals: Allocation[];
  /** Что осталось свободным. При `preliminary` свободными не является. */
  leftover: Money;
  /** Чего не хватило, если пришло меньше защищённой части. */
  shortfall: Money;
  /** Период защиты — показывается человеку вместе с суммой. */
  protect: ProtectWindow;
  /**
   * Расчёт предварительный: данных для распределения не хватает.
   *
   * При `true` цели не наполняются автоматически, а остаток **не называется
   * свободным**: без оценки расходов неизвестно, что из него — деньги повседневные нужды.
   */
  preliminary: boolean;
  /** Что не удалось посчитать и что для этого нужно. */
  unknowns: { field: string; reason: string; action: string }[];
}

/**
 * До какого дня защищаем повседневные расходы.
 *
 * До ближайшего прихода либо на срок, названный человеком. Если ни того, ни
 * другого нет — периода нет: выдумывать его нельзя, и расчёт становится
 * ограниченным (см. `distributeIncome`). Период — параметр распределения, а
 * не обещание, что денег хватит: при нехватке возвращается `shortfall`, а не
 * урезанные цели.
 */
function protectWindow(input: DistributionInput): ProtectWindow {
  if (input.protectDays !== undefined) {
    return {
      until: addDays(input.today, input.protectDays),
      days: input.protectDays,
      source: 'user',
    };
  }
  if (input.nextIncomeDate) {
    const days = Math.max(1, diffDays(input.today, input.nextIncomeDate));
    return { until: input.nextIncomeDate, days, source: 'horizon' };
  }
  return { until: null, days: null, source: 'unknown' };
}

/**
 * Разложить пришедшие деньги: платежи → жизнь → выбор человека → цели.
 *
 * Порядок неслучаен. Платежи и жизнь защищаются раньше целей, потому что
 * невыполненный платёж стоит процентов, а недоеденный месяц заканчивается
 * кредиткой — и обнуляет любой взнос в цель.
 */
export function distributeIncome(input: DistributionInput): DistributionResult {
  const protect = protectWindow(input);
  const unknowns: DistributionResult['unknowns'] = [];
  let rest = input.amount;

  /*
    1. Необеспеченные платежи.

    Когда период известен — все до его конца. Когда неизвестен — только те,
    что **уже наступили**: они точно должны быть оплачены, а будущие без срока
    требовать нельзя, иначе распределение снова начнёт защищать выдуманное
    окно. С начала месяца, а не с сегодня: просроченное неоплаченное деньги
    связывает так же, как завтрашнее.
  */
  const monthStart = ymd(
    Number(input.today.slice(0, 4)), Number(input.today.slice(5, 7)), 1,
  );
  const instances = paymentInstances({
    obligations: input.obligations,
    ...(input.reserves ? { reserves: input.reserves } : {}),
    today: input.today,
    from: protect.until ? input.today : monthStart,
    to: protect.until ?? input.today,
  }).filter((i) => !i.settled && i.remaining > ZERO);

  const obligations: DistributionResult['obligations'] = [];
  for (const inst of instances) {
    if (rest <= ZERO) break;
    const take = inst.remaining < rest ? inst.remaining : rest;
    obligations.push({
      instanceId: inst.id,
      label: inst.label,
      dueDate: inst.scheduledDate,
      amount: take,
      reason: `Платёж ${inst.scheduledDate}: не обеспечен`,
    });
    rest = sub(rest, take);
  }
  const obligationsNeeded = add(...instances.map((i) => i.remaining));

  // 2. Жизнь до конца периода защиты.
  //
  //    При неизвестных расходах сумма не выдумывается: остаток после платежей
  //    нельзя объявить свободным, потому что неизвестно, сколько из него —
  //    деньги на еду.
  let living: Money | null = null;
  /** Сколько жизни требуется на период — до всякого урезания по остатку. */
  let livingNeeded: Money = ZERO;
  if (protect.days === null) {
    // Неизвестно, на сколько растягивать: сколько жизни защищать — тоже.
    unknowns.push({
      field: 'horizon',
      reason: 'Дата следующего прихода неизвестна',
      action: 'Назови срок, на который растянуть деньги, — или подтверди дату прихода',
    });
  } else if (isKnown(input.needBaseline)) {
    livingNeeded = share(input.needBaseline.value, protect.days / 30);
    living = livingNeeded < rest ? livingNeeded : rest;
    rest = sub(rest, living);
  } else {
    unknowns.push({
      field: 'needBaseline',
      reason: input.needBaseline.reason,
      action: 'Уточни обычные траты — без них нельзя понять, что из остатка свободно',
    });
  }

  /*
    Нехватка считается от **требуемого**, а не от выданного.

    Здесь стояло `living` — то, что удалось выделить после платежей, — и
    нехватка схлопывалась в ноль ровно там, где она есть. Пришло 50 000, на
    платежи нужно 40 000, повседневные нужды 30 000: жизни доставалось 10 000, сумма
    «40 000 + 10 000» сходилась с приходом, и приложение отвечало «всё
    разложилось» вместо «не хватает 20 000». Ноль, полученный урезанием, — это
    не ноль.

    При неизвестных расходах в счёт идут только платежи: выдумывать жизнь
    нельзя, и об этом отдельно говорит `unknowns`.
  */
  const shortfall = clampPos(sub(
    add(obligationsNeeded, livingNeeded),
    input.amount,
  ));

  // 3. Выбор человека идёт до водопада: он меняет распределение, а не подпись.
  let choiceAllocation: Allocation | null = null;
  const alreadyAllocated: { goalId: string; amount: Money }[] = [];
  if (input.choice && rest > ZERO && living !== null) {
    const take = input.choice.amount < rest ? input.choice.amount : rest;
    if (take > ZERO) {
      const goal = input.goals.find(
        (g) => g.id === input.choice!.targetId || g.loanId === input.choice!.targetId,
      );
      choiceAllocation = {
        goalId: goal?.id ?? input.choice.targetId,
        goalName: goal?.name ?? (input.choice.kind === 'repay_debt' ? 'Досрочное гашение' : 'Подушка'),
        amount: take,
        reason: input.choice.kind === 'repay_debt'
          ? 'Выбрано гашение долга'
          : 'Выбрано пополнение подушки',
        step: 0,
      };
      rest = sub(rest, take);
      // Водопад не должен вернуть эти деньги обратно в другую цель:
      // потолок выбранной цели уменьшается ровно на выданное.
      if (goal) alreadyAllocated.push({ goalId: goal.id, amount: take });
    }
  }

  // 4. Остаток — по целям. Обязательные минимумы уже покрыты шагом 1.
  //    Без периода защиты цели не наполняются: неизвестно, сколько из остатка
  //    придётся прожить.
  const preliminary = living === null;
  const goals = preliminary || rest <= ZERO
    ? []
    : allocateIncome({
      amount: rest,
      goals: input.goals,
      accounts: input.accounts,
      creditTerms: input.creditTerms,
      monthlyNeed: input.monthlyNeed,
      today: input.today,
      includeDebtMinimums: false,
      alreadyAllocated,
      ...(input.loans ? { loans: input.loans } : {}),
    }).allocations;

  const toGoals = add(...goals.map((g) => g.amount));

  return {
    obligations,
    living,
    choice: choiceAllocation,
    goals,
    leftover: clampPos(sub(rest, toGoals)),
    shortfall,
    protect,
    preliminary,
    unknowns,
  };
}
